import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef} from "react";
import {cn, formatFileSize, getFlag, hasClass, isMobileDevice, isOnlyMobileDevice} from "../../../../utils/utils";
import CustomFilePlayerMenu from "./customFilePlayerMenu/customFilePlayerMenu";
import UseSubtitles from "./hooks/useSubtitles";
import './customFilePlayer.css';
import {shallowEqual} from "react-redux";
import {VlcUtils} from "../../movies/utils/vlcUtils";
import SquadflixTVSendContent from "./squadflixTVSendContent/squadflixTVSendContent";
import ButtonV2 from "../../../shared/button-v2/button-v2";
import {SquadflixLogsApi} from "../../../../apis/squadflix-logs/SquadflixLogsApi";

const CustomFilePlayer = React.forwardRef(({
                                               downloadInfo,
                                               imagePath,
                                               title,
                                               customRuntime,
                                               videoUrl,
                                               onClosePlayer,
                                               originalVideoUrl,
                                               subtitles,
                                               audios,
                                               streaming = false,
                                               item,
                                               onGoNextEpisode,
                                               squadFlixTvHook,
                                               onStatusChange
                                           }, ref) => {

    const [focusVideo, setFocusVideo] = React.useState(false);
    const videoRef = React.useRef(null);
    const videoContainerRef = React.useRef(null);
    const lastDownloadSize = React.useRef(0);
    const lastSpeedCheck = React.useRef(0);
    const [paused, setPaused] = React.useState(false);
    const [videoDuration, setVideoDuration] = React.useState(0);
    const [currentTime, setCurrentTime] = React.useState(0);
    const rangeDurationInput = React.useRef(null);
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const [volume, setVolume] = React.useState(() => {
        const saved = localStorage.getItem("customFilePlayerVolume");
        return saved !== null ? parseFloat(saved) : 1;
    });
    const [isMuted, setIsMuted] = React.useState(() => {
        const saved = localStorage.getItem("customFilePlayerVolume");
        return saved !== null && parseFloat(saved) === 0;
    });
    const [loadingData, setLoadingData] = React.useState(true);
    const [criticalPlaybackError, setCriticalPlaybackError] = React.useState("");
    const [showVlcFallback, setShowVlcFallback] = React.useState(false);
    const [copiedVlcUrl, setCopiedVlcUrl] = React.useState(false);
    const [audioTracks, setAudioTracks] = React.useState([]);
    const [openedPlayerMenu, setOpenedPlayerMenu] = React.useState(false);
    const [preferedLanguage, setPreferedLanguage] = React.useState(localStorage.getItem("preferedLanguage") ?? "spa");
    const [selectedTrackIndex, setSelectedTrackIndex] = React.useState(0);
    const [firstLoad, setFirstLoad] = React.useState(true);
    const [contextMenuPosition, setContextMenuPosition] = React.useState({x: -1000, y: -1000});
    const [timeTooltip, setTimeTooltip] = React.useState({x: -1000, y: -1000, time: 0});
    const streamingStartingTime = React.useRef(0);
    const breakLoadingTime = React.useRef(0);
    const waitingForTimeLight = React.useRef(0);
    const downloadProgress = React.useRef(0);
    const [selectedMenu, setSelectedMenu] = React.useState(null);
    const [selectedVideoType, setSelectedVideoType] = React.useState(streaming ? "streaming" : "original");
    const [selectedVideoUrl, setSelectedVideoUrl] = React.useState(videoUrl + "");
    const subtitlesContainer = React.useRef(null);
    const lastViewingLogSent = useRef(null);
    const [wideScreenMode, setWideScreenMode] = React.useState(false);

    // Añadido: estado y referencias para Chromecast
    const [isCasting, setIsCasting] = React.useState(false);
    const castSessionRef = React.useRef(null);
    const isMountedRef = React.useRef(true);
    const seekRequestIdRef = React.useRef(0);
    const loadingWatchdogTimeoutRef = React.useRef(null);
    const streamingErrorRetryCountRef = React.useRef(0);
    const streamingFallbackUsedRef = React.useRef(false);

    useEffect(() => {
        // Inicializa el SDK de Cast cuando esté disponible
        const initCast = () => {
            try {
                if (!window.cast || !window.cast.framework) return;
                const CastContext = window.cast.framework.CastContext;
                CastContext.getInstance().setOptions({
                    receiverApplicationId: window.chrome?.cast?.media?.DEFAULT_MEDIA_RECEIVER_APP_ID || 'CC1AD845',
                    autoJoinPolicy: window.chrome?.cast?.AutoJoinPolicy?.ORIGIN_SCOPED
                });
            } catch (err) {
                console.warn("Cast init error", err);
            }
        };

        if (window.cast && window.cast.framework) {
            initCast();
        } else {
            // El SDK invocará __onGCastApiAvailable cuando se haya cargado
            window.__onGCastApiAvailable = function (isAvailable) {
                if (isAvailable) initCast();
            };
        }

        return () => {
            try {
                if (window.__onGCastApiAvailable) window.__onGCastApiAvailable = undefined;
            } catch (e) {
            }
        };
    }, []);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            seekRequestIdRef.current += 1;
        };
    }, []);

    useEffect(() => {
        if (videoRef.current) {
            videoRef.current.volume = volume;
        }
    }, [volume]);

    const stopCastAndResume = async () => {
        try {
            const session = castSessionRef.current;
            if (!session) return;

            // intentar obtener tiempo remoto antes de terminar
            let remoteTime = 0;
            try {
                const media = session.getMediaSession && session.getMediaSession();
                if (media && typeof media.getEstimatedTime === 'function') {
                    remoteTime = media.getEstimatedTime();
                } else if (media && typeof media.currentTime !== 'undefined') {
                    remoteTime = media.currentTime;
                }
            } catch (e) {
                // ignore
            }

            try {
                session.endSession(true);
            } catch (e) {
                // algunos navegadores/SDK pueden no exponer endSession exactamente así
                try {
                    session.stop();
                } catch (err) {
                }
            }

            setIsCasting(false);
            castSessionRef.current = null;

            // Reanudar reproducción local en la posición remota
            if (videoRef.current) {
                videoRef.current.src = selectedVideoUrl ?? "";
                try {
                    videoRef.current.currentTime = remoteTime || currentTime || 0;
                } catch (e) {
                }
                try {
                    videoRef.current.play().catch(() => {
                    });
                } catch (e) {
                }
            }
        } catch (err) {
            console.error('Error stopping cast', err);
        }
    };

    const handleCast = async () => {
        try {
            if (isCasting) {
                await stopCastAndResume();
                return;
            }

            if (!window.cast || !window.cast.framework) {
                console.warn("Cast SDK no disponible");
                return;
            }

            const context = window.cast.framework.CastContext.getInstance();
            const session = await context.requestSession(); // abre el selector de dispositivos
            if (!session) return;

            castSessionRef.current = session;
            setIsCasting(true);

            // Determinar tipo de contenido
            const url = selectedVideoUrl ?? videoUrl ?? originalVideoUrl ?? "";
            const isHls = url.indexOf('.m3u8') !== -1 || url.indexOf('playlist.m3u8') !== -1;
            const contentType = isHls ? 'application/vnd.apple.mpegurl' : 'video/mp4';

            // Obtener namespaces de media del SDK
            const mediaNs = window.chrome?.cast?.media || window.cast?.media;
            if (!mediaNs) {
                console.warn('Media namespace no disponible en Cast SDK');
                return;
            }

            const MediaInfo = mediaNs.MediaInfo;
            const GenericMediaMetadata = mediaNs.GenericMediaMetadata;
            const LoadRequest = mediaNs.LoadRequest;

            const mediaInfo = new MediaInfo(url, contentType);
            const metadata = new GenericMediaMetadata();
            metadata.title = title ?? "";
            if (imagePath) {
                metadata.images = [{url: imagePath}];
            }
            mediaInfo.metadata = metadata;

            const request = new LoadRequest(mediaInfo);
            request.currentTime = currentTime || 0;
            request.autoplay = true;

            session.loadMedia(request).then(() => {
                try {
                    videoRef.current?.pause();
                } catch (e) {
                }
            }).catch(err => {
                console.error("Error al cargar media en Chromecast", err);
                setIsCasting(false);
                castSessionRef.current = null;
            });

            // Escuchar fin de session
            try {
                session.addUpdateListener && session.addUpdateListener((isAlive) => {
                    if (!isAlive) {
                        // session finalizada
                        setIsCasting(false);
                        castSessionRef.current = null;
                        // reanudar local (intentar recuperar tiempo remoto)
                        stopCastAndResume().catch(() => {
                        });
                    }
                });
            } catch (e) {
                // no crítico
            }

        } catch (err) {
            console.error("Error handleCast", err);
        }
    }

    const {currentSubtitle, setCurrentSubtitle, availableSubtitles, lastSubtitleTime} = UseSubtitles({
        subtitlesContainer, subtitles: subtitles ?? [], currentTime, itemId: item.serie?.id ?? item.id
    });

    const handleSelectVideoType = (type) => {
        if (selectedVideoType === type) {
            return;
        }
        localStorage.setItem(`preferedVideoType__${item.serie?.id ?? item.id}`, type);
        setSelectedVideoType(type);
    }

    const handleHideSidebarMenu = () => {
        setOpenedPlayerMenu(false);
        setSelectedMenu(null);
    }

    const handleShowSidebarMenu = (menu) => {
        setOpenedPlayerMenu(true);
        setSelectedMenu(menu);
        if (isOnlyMobileDevice()) {
            if (moveTimeout.current) {
                clearTimeout(moveTimeout.current);
            }
            setFocusVideo(false);
        }
    }

    useEffect(() => {
        const prefered = localStorage.getItem(`preferedVideoType__${item.serie?.id ?? item.id}`);
        if (prefered && (prefered === "streaming" || prefered === "original")) {
            setSelectedVideoType(prefered);
        } else {
            setSelectedVideoType(streaming ? "streaming" : "original");
        }
    }, [item, streaming]);

    const handleOpenVlc = async (e) => {
        VlcUtils.openInVlc(e, originalVideoUrl, "video");

        // En Safari/Chrome iOS la apertura de esquemas externos puede fallar silenciosamente.
        const ua = navigator.userAgent || navigator.vendor || window.opera;
        const isIOSBrowser = /iPhone|iPad|iPod/i.test(ua);
        if (isIOSBrowser) {
            setShowVlcFallback(true);
        }
    }

    const copyOriginalUrl = async () => {
        if (!originalVideoUrl) {
            return;
        }

        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(originalVideoUrl);
            } else {
                const temp = document.createElement("textarea");
                temp.value = originalVideoUrl;
                document.body.appendChild(temp);
                temp.select();
                document.execCommand("copy");
                document.body.removeChild(temp);
            }
            setCopiedVlcUrl(true);
            setTimeout(() => setCopiedVlcUrl(false), 2000);
        } catch (err) {
            console.error("Error copiando enlace para VLC", err);
        }
    }

    const isStreamingActive = useMemo(() => {
        return selectedVideoType === "streaming" && streaming;
    }, [streaming, selectedVideoType]);

    const saveCurrentTime = (currTime) => {
        if (loadingData || firstLoad) {
            return;
        }
        if (videoUrl) {
            localStorage.setItem(getCleanVideoUrl(videoUrl) + "-currentTime", currTime + (isStreamingActive ? streamingStartingTime.current : 0));
        }
        if (originalVideoUrl) {
            localStorage.setItem(getCleanVideoUrl(originalVideoUrl) + "-currentTime", currTime + (isStreamingActive ? streamingStartingTime.current : 0));
        }
    }

    useEffect(() => {
        setFirstLoad(true);
        setCriticalPlaybackError("");
        setShowVlcFallback(false);
        setCopiedVlcUrl(false);
        streamingErrorRetryCountRef.current = 0;
        streamingFallbackUsedRef.current = false;
        if (selectedVideoType === "streaming") {
            setSelectedVideoUrl(videoUrl);
        } else {
            setSelectedVideoUrl(originalVideoUrl ?? videoUrl);
        }
    }, [videoUrl, originalVideoUrl, selectedVideoType]);

    const lastStatusChangeTime = useRef(0);

    useEffect(() => {
        if (!paused) {
            const now = Date.now();
            if (now - lastStatusChangeTime.current < 1000) { // at least 1 second between status changes
                return;
            }
            lastStatusChangeTime.current = now;
        }
        onStatusChange?.({
            paused,
            currentTime,
            videoDuration,
            selectedTrackIndex,
            currentSubtitle,
            videoType: selectedVideoType
        });
    }, [onStatusChange, paused, currentTime, videoDuration, selectedTrackIndex, currentSubtitle, selectedVideoType]);

    useEffect(() => {
        if (!downloadInfo) {
            downloadProgress.current = 1;
        } else {
            downloadProgress.current = downloadInfo.progress - 0.05; //small 5% delay
        }

        if (rangeDurationInput.current) {
            rangeDurationInput.current.style.setProperty('--towersStreamViewerRunnableTrackWidth', `${downloadProgress.current * 100}%`);
        }
    }, [downloadInfo]);

    useImperativeHandle(ref, () => ({
        reload: () => {
            if (videoRef.current) {
                console.log("Reload video");
                const currentTime = videoRef.current.currentTime;
                videoRef.current.load();
                videoRef.current.currentTime = currentTime;
            }
        },
        play: () => {
            if (videoRef.current) {
                videoRef.current.play().then(() => {
                    setFocusVideo(true);
                }).catch(() => {
                });
            }
        },
        pause: () => {
            if (videoRef.current) {
                videoRef.current.pause();
                setFocusVideo(false);
            }
        },
        forward: (seconds) => {
            if (videoRef.current) {
                setVideoCurrentTime(currentTime + seconds);
            }
        },
        changeVideoType: (type) => {
            handleSelectVideoType(type);
        },
        selectSubtitle: (subtitle) => {
            setCurrentSubtitle(subtitle);
        },
        selectAudioTrack: (trackIndex) => {
            const track = audioTracks[trackIndex];
            if (selectedTrackIndex === trackIndex || !track) {
                return;
            }
            handleChangeAudioTrack(track.id);
        },
        setFullscreen: (value) => {
            setFullscreen(value);
        }
    }));

    const getCleanVideoUrl = (url) => {
        return url.split("&t=")[0];
    }

    useEffect(() => {

        if (!videoUrl || firstLoad || !videoRef.current) {
            return;
        }

        const saveCurTimeInterval = setInterval(() => {
            if (!videoRef.current) {
                return;
            }
            saveCurrentTime(videoRef.current.currentTime);
        }, 5000);

        return () => {
            clearInterval(saveCurTimeInterval);
        }

    }, [videoRef.current, videoUrl, firstLoad]);

    const downloadSpeed = useMemo(() => {
        if (!downloadInfo) {
            return NaN;
        }

        const newDownloadSize = downloadInfo.downloaded_size;
        const donwloadedBytes = newDownloadSize - lastDownloadSize.current;
        const now = Date.now();

        const speed = donwloadedBytes / ((now - lastSpeedCheck.current) / 1000);

        lastDownloadSize.current = newDownloadSize;
        lastSpeedCheck.current = now;

        return speed;

    }, [downloadInfo]);


    const handleUserInteraction = (e, ignoreFocus = false) => {
        if (!videoRef.current) return;

        if (contextMenuPosition.x !== -1000 || contextMenuPosition.y !== -1000) {
            setContextMenuPosition({x: -1000, y: -1000});
            if (isOnlyMobileDevice()) {
                setFocusVideo(true);
            }
            return;
        }
        if (openedPlayerMenu) {
            if (e && hasClass(e.target, ["towersStreamViewer__track-selector-modal"])) {
                return;
            }
            handleHideSidebarMenu();
            if (isOnlyMobileDevice()) {
                setFocusVideo(true);
            }
            return;
        }

        if (loadingData) return;

        // if click is in the input range, do nothing
        if (e && hasClass(e.target, ["towersStreamViewer__main__video-controls__time-range", "towersStreamViewer__icon-control", "fa-square-xmark"])) {
            return;
        }
        if (e && hasClass(e.target, ["towersStreamViewer__main__video-controls__top__options"])) {
            return;
        }
        if (e && hasClass(e.target, ["towersStreamViewer__main__video-controls__next-episode"])) {
            return;
        }

        if (focusVideo && !ignoreFocus) {
            handleMoveCursor();
            return;
        }

        if (Date.now() - lastClick.current < 200) {
            lastClick.current = 0;
            handleToggleFullscreen();

            if (timeoutClick.current) {
                clearTimeout(timeoutClick.current);
            }

            return;
        }


        lastClick.current = Date.now();

        timeoutClick.current = setTimeout(() => {
            handleMoveCursor();
            if (videoRef.current.paused) {
                try {
                    videoRef.current.play()
                } catch (e) {
                }
            } else {
                videoRef.current.pause();
            }
        }, 200);
    }

    const handleChangeAudioTrack = (trackId) => {
        const trackIndex = audioTracks.findIndex(t => t.id === trackId)

        if (selectedTrackIndex === trackIndex) {
            return;
        }

        const wasPlaying = !videoRef.current.paused; // Verifica si el video estaba reproduciéndose antes del cambio
        videoRef.current.pause();

        audioTracks.forEach(t => {
            t.enabled = t.id === trackId;
        });

        if (!isStreamingActive && videoRef.current?.audioTracks) {
            console.log(videoRef.current?.audioTracks, trackId);
            Array.from(videoRef.current?.audioTracks).forEach(t => {
                t.enabled = t.id === trackId;
            })
        }

        handleHideSidebarMenu();


        if (isStreamingActive) {
            const newUrl = getCleanVideoUrl(videoUrl) + "&t=" + currentTime + "&audioIndex=" + trackId;
            videoRef.current.src = newUrl;
            setSelectedVideoUrl(newUrl);
            videoRef.current.load();
        }

        if (wasPlaying) {
            videoRef.current.currentTime = videoRef.current.currentTime - 1; // Para que se actualice el audio
            videoRef.current.play().catch(() => {
            });
        }

        setSelectedTrackIndex(trackIndex);
        localStorage.setItem("preferedLanguage", audioTracks[trackIndex].language);
        localStorage.setItem(`preferedTrackForVideo__${item.serie?.id ?? item.id}`, audioTracks[trackIndex].label);
    }


    const handleLoadedDataVideo = () => {
        let auxAudioTracks = null;
        if (isStreamingActive) {
            auxAudioTracks = audios;
        } else {
            auxAudioTracks = Array.from(videoRef.current?.audioTracks ?? []).map((x, i) => {
                return {
                    ...audios[i],
                    id: x.id,
                }
            });
        }

        if (shallowEqual(auxAudioTracks, audioTracks)) {
            return;
        }

        setAudioTracks(auxAudioTracks ? [...auxAudioTracks] : []);
    }


    useEffect(() => {
        handleLoadedDataVideo();
    }, [audios])

    useEffect(() => {
        if (audioTracks.length > 1) {
            const savedTrackLabel = localStorage.getItem(`preferedTrackForVideo__${item.serie?.id ?? item.id}`) ?? "";
            const preferedLanguageTracks = audioTracks.filter(t => t.language === preferedLanguage);
            let preferedTrack = null;
            if (preferedLanguageTracks.length > 0 && savedTrackLabel) {
                preferedTrack = preferedLanguageTracks.find(t => t.label?.toLowerCase() === savedTrackLabel?.toLowerCase());
            }

            if (!preferedTrack && preferedLanguageTracks.length > 0) {
                preferedTrack = audioTracks[0];
            }

            if (!preferedTrack) {
                return;
            }

            handleChangeAudioTrack(preferedTrack.id);
        }
    }, [audioTracks, preferedLanguage]);

    const lastClick = React.useRef(0);
    const timeoutClick = React.useRef(null);
    const moveTimeout = React.useRef(null);

    useEffect(() => {
        return () => {
            if (timeoutClick.current) {
                clearTimeout(timeoutClick.current);
            }
            if (moveTimeout.current) {
                clearTimeout(moveTimeout.current);
            }
            if (loadingWatchdogTimeoutRef.current) {
                clearTimeout(loadingWatchdogTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!loadingData) {
            if (loadingWatchdogTimeoutRef.current) {
                clearTimeout(loadingWatchdogTimeoutRef.current);
            }
            return;
        }

        if (loadingWatchdogTimeoutRef.current) {
            clearTimeout(loadingWatchdogTimeoutRef.current);
        }

        // Safari iOS a veces deja `waiting` activo aunque el video ya está listo.
        loadingWatchdogTimeoutRef.current = setTimeout(() => {
            const video = videoRef.current;
            if (!video || !isMountedRef.current || !loadingData) {
                return;
            }

            const canRenderFrame = video.readyState >= 2;
            const canPlayLikely = video.readyState >= 3;
            const isActivelyAdvancing = !video.paused && !video.seeking && video.currentTime > 0;

            if (canRenderFrame || canPlayLikely || isActivelyAdvancing) {
                setLoadingData(false);
            }
        }, 2500);

        return () => {
            if (loadingWatchdogTimeoutRef.current) {
                clearTimeout(loadingWatchdogTimeoutRef.current);
            }
        };
    }, [loadingData, selectedVideoUrl]);

    const handleMoveCursor = () => {
        setFocusVideo(false);
        if (moveTimeout.current) {
            clearTimeout(moveTimeout.current);
        }

        moveTimeout.current = setTimeout(() => {
            if (videoRef.current?.paused || loadingData) {
                return;
            }
            setFocusVideo(true);
        }, 2000);
    }

    const handleLeaveCursor = () => {
        if (isOnlyMobileDevice()) {
            return;
        }
        if (videoRef.current?.paused || loadingData) {
            return;
        }
        setFocusVideo(true);
    }

    const isFullscreenActive = () => {
        return !!(document.fullscreenElement || document.webkitFullscreenElement || videoRef.current?.webkitDisplayingFullscreen);
    }

    const enterFullscreenSafe = async () => {
        const container = videoContainerRef.current;
        const video = videoRef.current;

        if (container?.requestFullscreen) {
            const result = container.requestFullscreen();
            if (result && typeof result.then === "function") {
                await result;
            }
            return true;
        }

        // Fallback para iOS Safari en elemento <video>.
        if (video && typeof video.webkitEnterFullscreen === "function") {
            video.webkitEnterFullscreen();
            return true;
        }

        return false;
    }

    const exitFullscreenSafe = async () => {
        const video = videoRef.current;

        if (document.fullscreenElement || document.webkitFullscreenElement) {
            if (document.exitFullscreen) {
                const result = document.exitFullscreen();
                if (result && typeof result.then === "function") {
                    await result;
                }
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            }
            return true;
        }

        if (video?.webkitDisplayingFullscreen && typeof video.webkitExitFullscreen === "function") {
            video.webkitExitFullscreen();
            return true;
        }

        return false;
    }

    const handleToggleFullscreen = async () => {
        if (!videoContainerRef.current) return;

        setWideScreenMode(false);

        try {
            const shouldEnter = !isFullscreenActive();
            if (shouldEnter) {
                await enterFullscreenSafe();
            } else {
                await exitFullscreenSafe();
            }

            const fullScreenNow = isFullscreenActive();
            setIsFullscreen(fullScreenNow);

            if (isOnlyMobileDevice() && window.screen.orientation && window.screen.orientation.lock) {
                window.screen.orientation.lock(fullScreenNow ? "landscape" : "portrait").catch(() => {
                });
            }
        } catch (e) {
            setIsFullscreen(isFullscreenActive());
        }
    }

    const setFullscreen = useCallback((value) => {
        if (value) {
            if (!isFullscreen) {
                handleToggleFullscreen();
            }
        } else {
            if (isFullscreen) {
                handleToggleFullscreen();
            }
        }
    }, [isFullscreen]);


    useEffect(() => {
        const syncFullscreenState = () => {
            if (!isMountedRef.current) {
                return;
            }
            setIsFullscreen(isFullscreenActive());
        };

        document.addEventListener("fullscreenchange", syncFullscreenState);
        document.addEventListener("webkitfullscreenchange", syncFullscreenState);

        const video = videoRef.current;
        video?.addEventListener("webkitbeginfullscreen", syncFullscreenState);
        video?.addEventListener("webkitendfullscreen", syncFullscreenState);

        return () => {
            document.removeEventListener("fullscreenchange", syncFullscreenState);
            document.removeEventListener("webkitfullscreenchange", syncFullscreenState);
            video?.removeEventListener("webkitbeginfullscreen", syncFullscreenState);
            video?.removeEventListener("webkitendfullscreen", syncFullscreenState);
        };
    }, []);

    const getMousePositionTime = (e) => {
        const rect = e.target.getBoundingClientRect();
        const min = rect.left;

        const width = rect.width;
        const value = ((e.clientX ?? (e.touches[0] ?? e.changedTouches[0]).clientX) - min + 1) / (width - 1) * videoDuration;

        return Math.max(0, Math.min(value, videoDuration))
    }

    const handleClickTimeRange = (e) => {
        e.preventDefault();
        setVideoCurrentTime(getMousePositionTime(e));
    }

    const handleRangeChange = (e, userInteraction = true) => {
        // change variable --towersStreamViewerRangeWidth value
        const range = e.target;
        const value = range.value;
        const newValue = value / videoDuration * 100;

        range.style.setProperty('--towersStreamViewerRangeWidth', `${newValue}%`);
    }

    const waitForCondition = (condition, {pollMs = 100, maxWaitMs = 8000} = {}) => {
        if (condition()) {
            return Promise.resolve(true);
        }

        return new Promise(resolve => {
            const startTime = Date.now();
            const interval = setInterval(() => {
                if (condition()) {
                    clearInterval(interval);
                    resolve(true);
                    return;
                }

                if ((Date.now() - startTime) >= maxWaitMs) {
                    clearInterval(interval);
                    resolve(false);
                }
            }, pollMs);
        });
    }

    const setVideoCurrentTime = async (time) => {
        if (!videoRef.current) {
            return;
        }

        const localSeekRequestId = ++seekRequestIdRef.current;

        breakLoadingTime.current = 1;

        if (downloadInfo && downloadProgress.current < 1) {
            const newTimeProgress = time / videoDuration;

            if (newTimeProgress > downloadProgress.current) {
                const lockReleased = await waitForCondition(() => waitingForTimeLight.current === 0, {
                    pollMs: 100,
                    maxWaitMs: 8000,
                });
                if (!lockReleased || !isMountedRef.current || localSeekRequestId !== seekRequestIdRef.current) {
                    return;
                }

                waitingForTimeLight.current = 1;
                breakLoadingTime.current = 0;

                const videoPaused = videoRef.current.paused;

                if (!videoPaused) {
                    videoRef.current.pause();
                }
                setLoadingData(true);

                const waitStart = Date.now();

                try {
                    while (newTimeProgress > downloadProgress.current) {
                        await new Promise(resolve => setTimeout(resolve, 400));

                        if (!isMountedRef.current || localSeekRequestId !== seekRequestIdRef.current) {
                            return;
                        }

                        if ((Date.now() - waitStart) > 15000) {
                            break;
                        }

                        rangeDurationInput.current?.style?.setProperty('--towersStreamViewerRangeWidth', `${newTimeProgress * 100}%`);

                        if (breakLoadingTime.current === 1) {
                            if (!videoPaused) {
                                videoRef.current.play().catch(() => {
                                });
                            }
                            return;
                        }
                    }
                } finally {
                    waitingForTimeLight.current = 0;
                    setLoadingData(false);
                }

                if (!videoPaused) {
                    videoRef.current.play().catch(() => {
                    });
                }
            }
        }

        if (isStreamingActive) {
            setSelectedVideoUrl(getCleanVideoUrl(videoUrl) + "&t=" + parseInt(time).toFixed(2) + (audios[selectedTrackIndex]?.id ? ("&audioIndex=" + audios[selectedTrackIndex].id) : ""));
            streamingStartingTime.current = parseInt(time);
        } else {
            console.log("Set time", time);
            videoRef.current.currentTime = time;
        }
        saveCurrentTime(time);
    }

    useEffect(() => {

        const handleClickKey = (e) => {
            if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) {
                return;
            }
            if (e.key === " ") {
                e.preventDefault();
                handleUserInteraction(undefined, true);
            } else if (e.key === "f") {
                e.preventDefault();
                handleToggleFullscreen();
            } else if (e.key === "m") {
                e.preventDefault();
                setIsMuted(!isMuted);
            } else if (e.key === "ArrowRight") {
                e.preventDefault();
                setVideoCurrentTime(currentTime + 10);
            } else if (e.key === "ArrowLeft") {
                e.preventDefault();
                setVideoCurrentTime(currentTime - 10);
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setVolume(v => {
                    const newVol = Math.min(1, parseFloat((v + 0.05).toFixed(2)));
                    localStorage.setItem("customFilePlayerVolume", newVol);
                    if (newVol > 0) {
                        setIsMuted(false);
                    }
                    return newVol;
                });
            } else if (e.key === "ArrowDown") {
                e.preventDefault();
                setVolume(v => {
                    const newVol = Math.max(0, parseFloat((v - 0.05).toFixed(2)));
                    localStorage.setItem("customFilePlayerVolume", newVol);
                    if (newVol === 0) {
                        setIsMuted(true);
                    }
                    return newVol;
                });
            }
        }

        document.addEventListener("keydown", handleClickKey);

        return () => {
            document.removeEventListener("keydown", handleClickKey);
        }

    }, [handleUserInteraction, selectedTrackIndex, isStreamingActive, handleToggleFullscreen, isMuted, setVideoCurrentTime, currentTime, volume]);

    const getParsedDuration = (duration) => {
        if (!duration) {
            return "--:--";
        }

        const durationDate = new Date(duration * 1000);

        const hours = durationDate.getUTCHours();
        let minutes = durationDate.getUTCMinutes();
        let seconds = durationDate.getUTCSeconds();
        const fillZeros = (num) => {
            return num < 10 ? "0" + num : num;
        }

        if (hours) {
            return `${hours}:${fillZeros(minutes)}:${fillZeros(seconds)}`;
        } else {
            return `${fillZeros(minutes)}:${fillZeros(seconds)}`;
        }
    }

    useEffect(() => {
        if (rangeDurationInput.current) {
            handleRangeChange({target: rangeDurationInput.current}, false);
        }
    }, [currentTime, isStreamingActive]);

    useEffect(() => {
        if (focusVideo) {
            setOpenedPlayerMenu(false);
            setContextMenuPosition({x: -1000, y: -1000});
        }
    }, [focusVideo]);

    const handleOpenTrackSelector = () => {
        handleShowSidebarMenu("trackSelector");
    }

    const handleOpenVideoSelector = () => {
        handleShowSidebarMenu("videoSelector");
    }

    const handleOpenSubtitlesSelector = () => {
        handleShowSidebarMenu("subtitlesSelector");
    }

    useEffect(() => {
        if (loadingData) {
            setFocusVideo(false)
        } else {
            if (moveTimeout.current) {
                clearTimeout(moveTimeout.current);
            }

            moveTimeout.current = setTimeout(() => {
                if (videoRef.current?.paused || loadingData) {
                    return;
                }
                setFocusVideo(true);
            }, 2000);
        }
    }, [loadingData]);

    const handleMouseInRangeTime = (e) => {
        if (!videoRef.current) {
            return;
        }

        setTimeTooltip({
            x: (e.clientX ?? e.touches[0].clientX) - 35,
            y: rangeDurationInput.current.getBoundingClientRect().top - 40,
            time: getMousePositionTime(e)
        });
    }

    const handleMouseLeaveRangeTime = (e) => {
        setTimeTooltip({x: -1000, y: -1000, time: 0})
        if (isOnlyMobileDevice()) {
            const time = getMousePositionTime(e);
            setVideoCurrentTime(time);
        }
    }

    const getCurrentTimeFromVideoUrl = (url) => {
        let time = url.split("&t=")[1];
        if (!time) {
            return 0;
        }

        if (isNaN(time)) {
            time = time.split("&audioIndex=")[0];

            if (isNaN(time)) {
                return 0;
            }
        }

        return parseInt(time);
    }

    const getMediaErrorDetails = (event) => {
        const video = event?.target ?? videoRef.current;
        const mediaError = video?.error;
        const code = mediaError?.code;
        const codeMap = {
            1: "MEDIA_ERR_ABORTED",
            2: "MEDIA_ERR_NETWORK",
            3: "MEDIA_ERR_DECODE",
            4: "MEDIA_ERR_SRC_NOT_SUPPORTED",
        };

        return {
            code: code ?? "unknown",
            codeName: codeMap[code] ?? "UNKNOWN_MEDIA_ERROR",
            message: mediaError?.message || "",
            src: video?.currentSrc || video?.src || selectedVideoUrl || "",
            networkState: video?.networkState,
            readyState: video?.readyState,
            currentTime: Math.floor(video?.currentTime || 0),
            selectedVideoType,
            isStreamingActive,
        };
    }

    const handleVideoError = (event) => {
        const details = getMediaErrorDetails(event);
        const isStreamingError = selectedVideoType === "streaming" && streaming;

        // Primer intento: reintento único para fallos transitorios de red/decode en iOS.
        if (isStreamingError && streamingErrorRetryCountRef.current < 1 && (details.code === 2 || details.code === 3)) {
            streamingErrorRetryCountRef.current += 1;
            setLoadingData(true);
            const retryUrl = `${getCleanVideoUrl(videoUrl)}&t=${details.currentTime || 0}&retry=${Date.now()}` +
                (audios[selectedTrackIndex]?.id ? (`&audioIndex=${audios[selectedTrackIndex].id}`) : "");
            setSelectedVideoUrl(retryUrl);
            console.error("[CustomFilePlayer] Streaming retry triggered", details);
            return;
        }

        // Fallback: si streaming no es compatible en iOS, cambia al modo original automáticamente.
        if (isStreamingError && originalVideoUrl && !streamingFallbackUsedRef.current) {
            streamingFallbackUsedRef.current = true;
            setLoadingData(true);
            const fallbackTime = details.currentTime || currentTime || 0;
            setSelectedVideoType("original");
            setSelectedVideoUrl(originalVideoUrl);
            saveCurrentTime(fallbackTime);
            console.error("[CustomFilePlayer] Streaming failed, fallback to original mode", details);
            return;
        }

        setLoadingData(false);
        const errorCodeText = details.codeName || "UNKNOWN_MEDIA_ERROR";
        const errorMessage = details.message ? ` (${details.message})` : "";
        setCriticalPlaybackError(`No se puede reproducir este video en el navegador (${errorCodeText}${errorMessage}).`);
        console.error("[CustomFilePlayer] Video error", details);
    }

    const handlePlayerTimeUpdate = (e) => {
        const curTime = e.target.currentTime + (isStreamingActive ? streamingStartingTime.current : 0);

        if (loadingData) {
            setLoadingData(false);
        }

        setCurrentTime(curTime);
        const vidDuration = isStreamingActive ? ((customRuntime ?? 0) * 60) : e.target.duration;
        setVideoDuration(vidDuration);

        // log each 2 minutes
        if (!lastViewingLogSent.current || (curTime - lastViewingLogSent.current) > 120) {
            lastViewingLogSent.current = curTime;
            const tmdbId = item.serie?.id ?? item.id;
            SquadflixLogsApi.logSquadflixView({
                tmdbId,
                season: item.season_number,
                episode: item.episode_number,
                duration: Math.floor(curTime)
            }).catch(e => {
                console.error("Error logging viewing", e);
            })
        }
    }

    if (squadFlixTvHook?.connectedReceptor) {
        return <SquadflixTVSendContent props={{
            downloadInfo,
            imagePath,
            title,
            customRuntime,
            videoUrl,
            onClosePlayer,
            originalVideoUrl,
            subtitles,
            audios,
            streaming,
            item,
            onGoNextEpisode,
            squadFlixTvHook
        }}/>
    }

    return (<div className="towersStreamViewer__main" ref={videoContainerRef}
                 onClick={handleUserInteraction}
                 onPointerLeave={handleLeaveCursor}
                 onPointerMove={() => !isOnlyMobileDevice() && handleMoveCursor()}
                 onTouchMove={() => isOnlyMobileDevice() && handleMoveCursor()}
                 onContextMenu={(e) => {
                     e.preventDefault();
                     if (loadingData) {
                         return;
                     }
                     if (isOnlyMobileDevice() && moveTimeout.current) {
                         clearTimeout(moveTimeout.current);
                     }
                     setContextMenuPosition({x: e.clientX, y: e.clientY});
                 }}
    >
        <CustomFilePlayerMenu title={title} clickPosition={contextMenuPosition} videoUrl={videoUrl}
                              downloading={!!downloadInfo}
                              downloadUrl={originalVideoUrl ?? videoUrl}/>
        <div style={{
            position: "fixed",
            left: timeTooltip.x,
            top: timeTooltip.y,
            width: "70px",
            zIndex: 6,
            height: "32px",
            background: "rgba(0,0,0,0.8)",
            pointerEvents: "none",
            color: "white",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "10px",
        }}>
            {getParsedDuration(timeTooltip.time)}
        </div>
        {selectedMenu && <div className="towersStreamViewer__track-selector-modal" style={{
            right: openedPlayerMenu ? 0 : "-100%", opacity: openedPlayerMenu ? 1 : 0,
        }}>
            <div className="towersStreamViewer__track-selector-modal__content">
                {selectedMenu === "trackSelector" &&
                    <div className="towersStreamViewer__track-selector-modal__content__list">
                        {audioTracks.map((track, i) => {
                            let audioTrack = track;
                            return <div key={i}
                                        className="towersStreamViewer__track-selector-modal__content__list__track"
                                        onClick={() => {
                                            if (selectedTrackIndex === i) {
                                                return;
                                            }
                                            handleChangeAudioTrack(track.id);
                                        }}>
                                <div
                                    className="towersStreamViewer__track-selector-modal__content__list__item__name">
                                    {audioTrack.label ?? audioTrack.title}
                                </div>
                                <div
                                    className="towersStreamViewer__track-selector-modal__content__list__item__lang">
                                    {audioTrack.language}&nbsp;<span className={getFlag(audioTrack.language)}></span>
                                    {selectedTrackIndex === i && <>&nbsp;<i
                                        className="fa-solid fa-volume-high"></i></>}
                                </div>
                            </div>
                        })}
                    </div>}
                {selectedMenu === "subtitlesSelector" &&
                    <div className="towersStreamViewer__track-selector-modal__content__list">
                        <div className="towersStreamViewer__track-selector-modal__content__list__track"
                             onClick={() => {
                                 setCurrentSubtitle(null);
                             }}>
                            <div
                                className="towersStreamViewer__track-selector-modal__content__list__item__name">
                                Sin Subtítulos
                            </div>
                        </div>
                        {availableSubtitles.map((sub, i) => {
                            return <div key={i}
                                        className="towersStreamViewer__track-selector-modal__content__list__track"
                                        onClick={() => {
                                            if (currentSubtitle?.id === sub.id) {
                                                return;
                                            }
                                            setCurrentSubtitle(sub);
                                        }}>
                                <div
                                    className="towersStreamViewer__track-selector-modal__content__list__item__name">
                                    {sub.title}
                                </div>
                                <div
                                    className="towersStreamViewer__track-selector-modal__content__list__item__lang">
                                    {sub.lang}&nbsp;<span className={getFlag(sub.lang)}></span>
                                    {currentSubtitle?.id === sub.id && <>&nbsp;<i
                                        className="fa-solid fa-closed-captioning"></i></>}
                                </div>
                            </div>
                        })}
                    </div>}
                {selectedMenu === "videoSelector" &&
                    <div className="towersStreamViewer__track-selector-modal__content__list">
                        <div
                            className={cn("towersStreamViewer__track-selector-modal__content__list__track", selectedVideoType === "streaming" ? "selected" : "")}
                            onClick={() => {
                                if (selectedVideoType === "streaming") {
                                    return;
                                }
                                saveCurrentTime(videoRef.current.currentTime);
                                handleSelectVideoType("streaming");
                            }}>
                            <div
                                className="towersStreamViewer__track-selector-modal__content__list__item__name">
                                Streaming
                                {selectedVideoType === "streaming" && <>&nbsp;<i
                                    className="fa-solid fa-play"></i></>}
                            </div>
                            <div
                                className="towersStreamViewer__track-selector-modal__content__list__item__lang">
                                Mayor Compatibilidad
                            </div>
                            <div
                                className="towersStreamViewer__track-selector-modal__content__list__item__description">
                                Maxima compatibilidad de audio y video con navegadores
                            </div>
                        </div>
                        <div
                            className={cn("towersStreamViewer__track-selector-modal__content__list__track", selectedVideoType === "original" ? "selected" : "")}
                            onClick={() => {
                                if (selectedVideoType === "original") {
                                    return;
                                }
                                if (!loadingData && !firstLoad) {
                                    saveCurrentTime(videoRef.current.currentTime);
                                }
                                handleSelectVideoType("original");
                            }}>
                            <div
                                className="towersStreamViewer__track-selector-modal__content__list__item__name">
                                Lossless
                                {selectedVideoType === "original" && <>&nbsp;<i
                                    className="fa-solid fa-play"></i></>}
                            </div>
                            <div
                                className="towersStreamViewer__track-selector-modal__content__list__item__lang">
                                Calidad Original
                            </div>
                            <div
                                className="towersStreamViewer__track-selector-modal__content__list__item__description">
                                <i className="fa-solid fa-triangle-exclamation"></i> Puede no ser compatible con
                                todos los navegadores
                            </div>
                        </div>
                    </div>}
            </div>
        </div>}
        <div
            className={cn("towersStreamViewer__main__video-controls", focusVideo ? "" : "towersStreamViewer__main__video-controls-show")}>
            <div className="towersStreamViewer__main__video-controls__top">
                <div
                    className={cn("towersStreamViewer__main__download-info")}>
                    <div className="towersStreamViewer__main__download-info__content">
                        <div className="towersStreamViewer__main__download-info__content__title">
                            {!!downloadInfo?.downloading && <span>Descargando....</span>}
                        </div>
                        {downloadInfo && !!downloadInfo.downloading &&
                            <div className="towersStreamViewer__main__download-info__content__download">
                                <div className="towersStreamViewer__main__download-info__content__progress">
                                    {(downloadInfo?.progress * 100).toFixed(2)}%
                                </div>
                                <div className="towersStreamViewer__main__download-info__content__speed">
                                    {formatFileSize(downloadSpeed)}/s
                                </div>
                                <div>
                                    {formatFileSize(downloadInfo.downloaded_size)} / {formatFileSize(downloadInfo.total_size)}
                                </div>
                            </div>}
                        {downloadInfo && !downloadInfo.downloading && !!downloadInfo.converting &&
                            <div className="towersStreamViewer__main__download-info__content__download">
                                <div className="towersStreamViewer__main__download-info__content__progress">
                                    Procesando conversión <i className="fa-solid fa-spinner fa-spin"></i>
                                </div>
                            </div>}
                        {lastSubtitleTime > 0 &&
                            <div className="towersStreamViewer__main__download-info__content__download">
                                <div className="towersStreamViewer__main__download-info__content__progress">
                                    Subtítulos: {((lastSubtitleTime / videoDuration) * 100).toFixed(0)}%
                                </div>
                            </div>

                        }
                    </div>
                </div>
                <div className="towersStreamViewer__main__video-controls__top__options">
                    {false &&
                        <div className="custom-file-player-icon custom-file-player-icon--vlc" onClick={handleOpenVlc}>
                            <img className="" src="https://upload.wikimedia.org/wikipedia/commons/3/38/VLC_icon.png"
                                 height={20} width={20}/>
                        </div>
                    }
                    {false && <i onClick={handleCast}
                                 className={"custom-file-player-icon fa-brands fa-chromecast" + (isCasting ? ' towersStreamViewer__casting' : '')}></i>}
                    {streaming &&
                        <i className="custom-file-player-icon fa-regular fa-file-video"
                           onClick={handleOpenVideoSelector}></i>
                    }
                    {availableSubtitles.length > 0 &&
                        <i className="custom-file-player-icon fa-regular fa-closed-captioning"
                           onClick={handleOpenSubtitlesSelector}></i>
                    }
                    {audioTracks.length > 1 &&
                        <i className="custom-file-player-icon fa-solid fa-headphones"
                           onClick={handleOpenTrackSelector}></i>
                    }
                    {onClosePlayer &&
                        <i className="fa-solid fa-square-xmark fa-2xl towersStreamViewer__main__video-controls__top__options__opt"
                           onClick={onClosePlayer}></i>}
                </div>
            </div>
            <div className="towersStreamViewer__main__video-controls__bottom">
                <div className="towersStreamViewer__main__video-controls__next-episode">
                    {item.nextEpisode &&
                        <ButtonV2 forceDarkMode={"dark"} onClick={onGoNextEpisode}>
                            Siguiente Episodio <i className="fa-solid fa-chevron-right fa-lg ps-3"></i>
                        </ButtonV2>
                    }
                </div>

                {!(isOnlyMobileDevice() && isMobileDevice()) &&
                    <div className="towersStreamViewer__icon-control isLast towersStreamViewer__volume-container">
                        <div className="towersStreamViewer__volume-slider-wrapper">
                            <input
                                type="range"
                                min="0"
                                max="1"
                                step="0.05"
                                value={isMuted ? 0 : volume}
                                onClick={(e) => e.stopPropagation()}
                                onChange={(e) => {
                                    const newVol = parseFloat(e.target.value);
                                    setVolume(newVol);
                                    localStorage.setItem("customFilePlayerVolume", newVol);
                                    if (newVol > 0 && isMuted) {
                                        setIsMuted(false);
                                    } else if (newVol === 0 && !isMuted) {
                                        setIsMuted(true);
                                    }
                                }}
                                style={{ '--volume-percent': `${(isMuted ? 0 : volume) * 100}%` }}
                                className="towersStreamViewer__volume-slider"
                            />
                        </div>
                        <div className="towersStreamViewer__volume-button">
                            {isMuted || volume === 0 ? (
                                <i className="custom-file-player-icon fa-solid fa-volume-mute"
                                   onClick={(e) => {
                                       e.stopPropagation();
                                       setIsMuted(false);
                                       if (volume === 0) {
                                           setVolume(0.5);
                                           localStorage.setItem("customFilePlayerVolume", 0.5);
                                       }
                                   }}></i>
                            ) : volume <= 0.5 ? (
                                <i className="custom-file-player-icon fa-solid fa-volume-low"
                                   onClick={(e) => {
                                       e.stopPropagation();
                                       setIsMuted(true);
                                   }}></i>
                            ) : (
                                <i className="custom-file-player-icon fa-solid fa-volume-high"
                                   onClick={(e) => {
                                       e.stopPropagation();
                                       setIsMuted(true);
                                   }}></i>
                            )}
                        </div>
                    </div>}
                {isOnlyMobileDevice() && isMobileDevice() && isFullscreen &&
                    <div className="towersStreamViewer__icon-control isLast">
                        {!wideScreenMode &&
                            <i className="custom-file-player-icon fa-solid fa-up-right-and-down-left-from-center"
                               onClick={() => setWideScreenMode(true)}></i>}
                        {wideScreenMode &&
                            <i className="custom-file-player-icon fa-solid fa-down-left-and-up-right-to-center"
                               onClick={() => setWideScreenMode(false)}></i>}
                    </div>}
                <div className="towersStreamViewer__icon-control">
                    <span className="towersStreamViewer__duration-info">
                        {getParsedDuration(currentTime)}
                    </span>
                </div>
                <input ref={rangeDurationInput} onChange={e => handleRangeChange(e, false)}
                       value={currentTime} max={videoDuration} min={0}
                       onClick={handleClickTimeRange} type={"range"}
                       onPointerMove={(e) => !isOnlyMobileDevice() && handleMouseInRangeTime(e)}
                       onTouchMove={(e) => isOnlyMobileDevice() && handleMouseInRangeTime(e)}
                       onPointerLeave={(e) => !isOnlyMobileDevice() && handleMouseLeaveRangeTime(e)}
                       onTouchEnd={(e) => isOnlyMobileDevice() && handleMouseLeaveRangeTime(e)}
                       className="towersStreamViewer__main__video-controls__time-range"/>
                <div className="towersStreamViewer__icon-control">
                    <span className="towersStreamViewer__duration-info">
                        {getParsedDuration(videoDuration)}
                    </span>
                </div>
                <div className="towersStreamViewer__icon-control">
                    {!isFullscreen && <i className="custom-file-player-icon fa-solid fa-expand"
                                         onClick={handleToggleFullscreen}></i>}
                    {isFullscreen && <i className="custom-file-player-icon fa-solid fa-compress"
                                        onClick={handleToggleFullscreen}></i>}
                </div>
            </div>
            <div className="towersStreamViewer__main__video-controls__center">
                {!loadingData && <>
                    {paused ? <i className="fa-solid fa-play"></i> : <i className="fa-solid fa-pause"></i>}
                </>}
                {loadingData && <i className="fa-solid fa-spinner fa-spin"></i>}
            </div>

            {!!criticalPlaybackError &&
                <div style={{
                    position: "absolute",
                    left: "50%",
                    top: "50%",
                    transform: "translate(-50%, -50%)",
                    background: "rgba(0,0,0,0.85)",
                    color: "white",
                    borderRadius: "12px",
                    padding: "14px 16px",
                    maxWidth: "min(92vw, 640px)",
                    width: "fit-content",
                    zIndex: 10,
                    textAlign: "center",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
                }}>
                    <div style={{marginBottom: originalVideoUrl ? 12 : 0}}>
                        <i className="fa-solid fa-triangle-exclamation text-warning me-2"></i>
                        {criticalPlaybackError}
                    </div>
                    {originalVideoUrl &&
                        <center>
                            <ButtonV2 forceDarkMode={"dark"} onClick={handleOpenVlc}>
                                Abrir archivo original en VLC <i className="fa-solid fa-up-right-from-square ms-2"></i>
                            </ButtonV2>

                            {showVlcFallback &&
                                <>
                                    <div style={{marginTop: 10, fontSize: "0.9rem", opacity: 0.9}}>
                                        Si no se abre VLC automáticamente, copia este enlace y pégalo en VLC.
                                    </div>
                                    <div style={{marginTop: 8, wordBreak: "break-all", fontSize: "0.82rem", opacity: 0.9}}>
                                        {originalVideoUrl}
                                    </div>
                                    <div style={{marginTop: 10}}>
                                        <ButtonV2 forceDarkMode={"dark"} onClick={copyOriginalUrl}>
                                            {copiedVlcUrl ? "Enlace copiado" : "Copiar enlace"}
                                            <i className="fa-regular fa-copy ms-2"></i>
                                        </ButtonV2>
                                    </div>
                                </>
                            }
                        </center>
                    }
                </div>
            }
        </div>
        <div className="customFilePlayer__subtitles" ref={subtitlesContainer}>

        </div>

        <video id="towersStreamViewer-player" className={cn({"custom-file-player__video-wide": wideScreenMode})}
               ref={videoRef} preload={"metadata"}
               playsInline
               muted={isMuted}
               onClick={handleUserInteraction}
               onCanPlay={(e) => {
                   if (firstLoad) {
                       const savedTime = localStorage.getItem(getCleanVideoUrl(selectedVideoUrl) + "-currentTime");
                       console.log("Saved time", savedTime);
                       if (savedTime) {
                           setVideoCurrentTime(savedTime);
                       }

                       if (!isFullscreen) {
                           handleToggleFullscreen();
                       }
                   }
                   if (videoRef.current) {
                       videoRef.current.volume = volume;
                   }
                   setFirstLoad(false);
                   setCriticalPlaybackError("");
                   setLoadingData(false);
               }}
               onSeeking={() => {
                   console.log('seeking')
               }}
               onWaiting={() => {
                   setLoadingData(true);
               }}
               onLoadCapture={() => {
                   console.log('load capture')
               }}
               onLoadStart={() => {
                   console.log('load start')
               }}
               onLoadedData={(e) => {
                   if (isStreamingActive) {
                       streamingStartingTime.current = getCurrentTimeFromVideoUrl(e.target.src);
                   }
                   if (videoRef.current) {
                       videoRef.current.volume = volume;
                   }

                   setCriticalPlaybackError("");
                   setLoadingData(false);
                   handleLoadedDataVideo();
               }}
               onPlay={() => {
                   setPaused(false);
               }}
               onPause={() => {
                   setPaused(true);
               }}
               onError={(e) => {
                   handleVideoError(e);
               }}
               onTimeUpdate={handlePlayerTimeUpdate}
               src={selectedVideoUrl ?? ""} autoPlay>
        </video>
    </div>);

});

export default CustomFilePlayer;
